import { Capacitor } from "@capacitor/core";
import { toast } from "sonner";
import { androidAlbumIdentifier } from "@/lib/saveMedia";

// Affiches générées (canvas → blob: URL) : lecture directe — blob:/data: n'acceptent pas la clé de cache « ?dl= » de fetchMediaBlob.
const localBlob = async (url) => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.blob();
};
const blobToDataUrl = (blob) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(blob); });
const isUnimplemented = (e) => e?.code === "UNIMPLEMENTED" || /not implemented|not available/i.test(String(e?.message || e || ""));
const isCancel = (e) => (e && e.name) === "AbortError" || /cancel|abort/i.test(String(e?.message || e || ""));

// Enregistre une affiche : app native → galerie (plugin Media, data URL, album Yalola) ; app sans le plugin → feuille du
// système ; web → téléchargement. Un <a download> sur un blob: ne fait RIEN dans la WebView Android (pas de gestionnaire
// de téléchargements) — c'était le bouton « Télécharger » muet des cartes de partage.
// Retourne "gallery" | "shared" | "cancelled" | "denied" | "downloaded".
export async function savePosterToGallery({ url, fileName = "yalola-affiche.png" } = {}) {
  if (!url) throw new Error("savePosterToGallery: url manquante");
  const blob = await localBlob(url);
  if (!Capacitor.isNativePlatform()) {
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href; a.download = fileName; a.rel = "noopener";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 3000);
    return "downloaded";
  }
  try {
    const { Media } = await import("@capacitor-community/media");
    const opts = { path: await blobToDataUrl(blob), fileName: fileName.replace(/\.[a-z0-9]+$/i, "") };
    if (Capacitor.getPlatform() === "android") opts.albumIdentifier = await androidAlbumIdentifier(Media);
    await Media.savePhoto(opts);
    return "gallery";
  } catch (e) {
    if (e?.code === "accessDenied") return "denied";
    if (!isUnimplemented(e)) throw e;
  }
  const { saveOrShareFile } = await import("@/lib/nativeShare");
  const how = await saveOrShareFile({ blob, fileName, title: "Yalola" });
  return how === "cancelled" ? "cancelled" : "shared";
}

// Partage l'affiche ELLE-MÊME (image) avec le texte + le lien en légende : app native → feuille du système (WhatsApp
// Android affiche l'image avec la légende dessous) ; web avec partage de fichiers → idem ; sinon (PC) → texte + lien.
// Retourne "shared" | "cancelled" | "link".
export async function sharePosterImage({ url, text = "", link = "", fileName = "yalola-affiche.png", title = "Yalola", copiedMessage = "Lien copié 🔗" } = {}) {
  const caption = [text, link].filter(Boolean).join("\n");
  if (url && Capacitor.isNativePlatform()) {
    const blob = await localBlob(url);
    const { Filesystem, Directory } = await import("@capacitor/filesystem");
    const { Share } = await import("@capacitor/share");
    const path = `${fileName.replace(/\.[a-z0-9]+$/i, "")}-${Date.now()}.png`;
    await Filesystem.writeFile({ path, data: String(await blobToDataUrl(blob)).split(",")[1], directory: Directory.Cache });
    const { uri } = await Filesystem.getUri({ path, directory: Directory.Cache });
    try { await Share.share({ title, text: caption || undefined, files: [uri] }); return "shared"; } catch (e) { if (isCancel(e)) return "cancelled"; throw e; }
  }
  if (url && typeof navigator !== "undefined" && navigator.share && navigator.canShare) {
    try {
      const blob = await localBlob(url);
      const file = new File([blob], fileName, { type: blob.type || "image/png" });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: caption || undefined, title });
        return "shared";
      }
    } catch (e) { if (isCancel(e)) return "cancelled"; }
  }
  const { shareImageOrLink } = await import("@/lib/nativeShare");
  await shareImageOrLink({ title, text, url: link, copiedMessage });
  return "link";
}

const toastSaved = (t, how) => {
  if (how === "gallery") toast.success(t("viewer.saved_gallery", "Enregistré dans ta galerie"));
  else if (how === "downloaded") toast.success(t("viewer.download_started", "Téléchargement lancé"));
  else if (how === "denied") toast.error(t("viewer.gallery_denied", "Autorise l'accès aux photos dans les réglages du téléphone pour enregistrer."));
  else if (how === "error") toast.error(t("viewer.download_failed", "Téléchargement impossible. Réessaie."));
};

// Bouton « Télécharger » des cartes de partage (profil, église, événement, groupe, direct, audio) : enregistrement + toast.
export async function downloadPoster({ url, fileName, t = (k, d) => d } = {}) {
  if (!url) return null;
  try { const how = await savePosterToGallery({ url, fileName }); toastSaved(t, how); return how; }
  catch { toastSaved(t, "error"); return "error"; }
}

// Bouton « Partager » des cartes : l'image + texte + lien ; en cas d'échec de l'image → texte + lien.
export async function sharePoster({ url, text, link, fileName, title, copiedMessage, t = (k, d) => d } = {}) {
  try { return await sharePosterImage({ url, text, link, fileName, title, copiedMessage }); }
  catch {
    const { shareImageOrLink } = await import("@/lib/nativeShare");
    const ok = await shareImageOrLink({ title, text, url: link, copiedMessage });
    if (!ok) toast.error(t("viewer.download_failed", "Téléchargement impossible. Réessaie."));
    return "link";
  }
}
